import {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { format } from 'date-fns';

import {
    BaseEvent,
    CoachingEventStatus,
    CoachingEventType,
    CoachingRole,
    CommitmentDto,
    CommitmentEvent,
    CommitmentPostDto,
    DeliveryType,
    EventParticipant,
    EventParticipantDto,
    FocusDto,
    GoalModel,
    GoalStatus,
    InteractionSummaryDto,
    LinkedInteractionDto,
    NoteAndCommentary,
    ObservationSummaryDto,
} from '../../coaching-base/general';
import { NoteDetailFormValue } from '../../coaching-note/note-detail-form-value.model';
import {
    manageNotes,
    noteDetailFormValueReverseTransformer,
    noteDetailFormValueTransformer,
} from '../../coaching-note/note-detail.form';
import {
    CmpsFormField,
    CmpsFormFields,
} from '../../coaching-observation/experimental.form';
import { focusesMatch } from '../../coaching-tags/focuses-match.function';
import { pluck } from '../../store.decorator';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import {
    ObservationFormValue,
    observationFormValueTransformer,
} from '../commitment-observations/observations.commitment.form';
import { GoalsCollection } from '../goals-collection.model';

export interface CommitmentFormFields extends CmpsFormFields {
    DELIVERY_TYPE: CmpsFormField;
    FOCUS: CmpsFormField;
    OBSERVATIONS: CmpsFormField;
    NOTES: CmpsFormField;
    DATE: CmpsFormField;
    DURATION: CmpsFormField;
    GOALS_COLLECTION: CmpsFormField; // TODO rename to GOALS
    PREVIOUS_DRAFT_ID: CmpsFormField;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CommitmentFormFields: CommitmentFormFields = {
    DELIVERY_TYPE: {
        name: 'deliveryType',
        label: TranslationKeyMap._9419_TYPE_,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
        },
    },
    FOCUS: {
        name: 'focus',
        label: '',
        validators: [Validators.required], // TODO change to runtime validator in order to set specific message for create vs. update
        validationMsgs: {
            required:
                TranslationKeyMap._9507_A_COMMITMENT_CANNOT_BE_CREATED_UPDATED_WITHOUT_A_FOCUS_,
            activeFocus:
                TranslationKeyMap._9478_THE_FOCUS_YOU_HAVE_SELECTED_IS_ALREADY_A_FOCUS_FOR_AN_ACTIVE_COMMITMENT,
            focusChange: TranslationKeyMap._9477_THE_FOCUS_CANNOT_BE_CHANGED_,
        },
    },
    OBSERVATIONS: {
        name: 'observations',
        label: '',
    },
    NOTES: {
        name: 'notes',
        label: '',
    },
    DATE: {
        name: 'date',
        label: '',
        validators: [Validators.required],
        validationMsgs: {
            required: TranslationKeyMap._9511_DATE_IS_REQUIRED_,
            ngbDate: TranslationKeyMap._9512_INVALID_DATE_,
        },
    },
    DURATION: {
        name: 'duration',
        label: '',
    },
    GOALS_COLLECTION: {
        name: 'goalsCollection',
        label: '',
    },
    PREVIOUS_DRAFT_ID: {
        name: 'previousDraftId',
        label: '',
    },
};

// TODO same check needs to be added to the api
/** ensures that the focus is not changed when the commitment is no longer in draft */
export function focusChangeValidator(commitment: CommitmentDto): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (
            commitment.statusId !== CoachingEventStatus.DRAFT &&
            !focusesMatch(commitment.focus, control.value)
        ) {
            return { focusChange: true };
        }
        return null;
    };
}

export function focusActiveValidator(
    hasActiveCmt: boolean,
    commitment: CommitmentDto,
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (hasActiveCmt && commitment.statusId === CoachingEventStatus.DRAFT) {
            return { activeFocus: true };
        }
        return null;
    };
}

export interface CommitmentFormValue {
    deliveryType: DeliveryType;
    focus: FocusDto;
    observations: ObservationFormValue[];
    notes: NoteDetailFormValue[];
    date: Date;
    duration: number;
    goalsCollection?: GoalsCollection;
    previousDraftId: string;
}

export function commitmentFormValueTransformer(
    deliveryType: DeliveryType,
    focus: FocusDto,
    observations: ObservationSummaryDto[],
    comments: NoteAndCommentary[],
    date?: Date,
    goalsCollection?: GoalsCollection,
    previousDraftId?: string,
): CommitmentFormValue {
    return {
        deliveryType,
        focus,
        observations: (observations || []).map(observationFormValueTransformer),
        // TODO revise
        notes: (comments || []).map((e) =>
            noteDetailFormValueTransformer({
                noteId: e.noteId,
                noteCategoryId: e.noteCategoryId,
                contents: e.contents,
                createdBy: null,
                createdOn: e.createdOn,
                published: null,
                publishedOn: null,
                diffItemId: e.diffItemId,
                updatedOn: e.updatedOn,
                createdByFirstName: e.createdByFirstName,
                createdByLastName: e.createdByLastName,
                updatedBy: e.updatedBy,
                updatedByFirstName: e.updatedByFirstName,
                updatedByLastName: e.updatedByLastName,
            }),
        ),
        date: date || new Date(),
        duration: 0,
        goalsCollection, // goalsCollectionTransformer(goalsCollection)
        previousDraftId,
    };
}

export function commitmentFormValueReverseTransformer(
    model: CommitmentFormValue,
    commitment: CommitmentDto,
    event: CommitmentEvent,
    participants: EventParticipantDto[],
    userId: number,
    isGeneric: boolean,
    activeMetricId: number,
): CommitmentPostDto {
    const nextParticipants = participants.map<EventParticipant>((e) => ({
        eventId: event.eventId != null ? event.eventId : 0, // reasons
        userId: e.id,
        coachingRoleId: e.coachingRoleId,
    }));

    const performerId = nextParticipants.find(
        (ee) => ee.coachingRoleId === CoachingRole.PERFORMER,
    ).userId;

    const nextEvent: BaseEvent = {
        eventId: 0, // reasons
        eventTypeId: event.eventTypeId,
        deliveryTypeId: model.deliveryType,
        sessionDate: event.sessionDate, // TODO revise. for commitment updates api will ignore submitted session date and use datetime.utcnow
        duration: event.duration + model.duration,
        shareWithPerformer: false, // TODO revise. always false. needs to be explicitly set to true
        published: false, // TODO revise. always false. needs to be explicitly set to true
        startTime: event.startTime,
        endTime: new Date(),
        createdBy: event.createdBy != null ? event.createdBy : userId,
        createdOn: event.createdOn || new Date(),
        updatedBy:
            event.updatedBy != null
                ? event.updatedBy
                : event.createdOn
                ? userId
                : null,
        updatedOn: event.updatedOn || (event.createdOn ? new Date() : null),
        participants: nextParticipants,
    };

    if (commitment.statusId === CoachingEventStatus.ACTIVE) {
        // commitment is active. check if the update event was created and is latest event
        // if it is an update event and it's already published, a new event is needed
        if (
            event.eventTypeId !== CoachingEventType.COMMITMENT_UPDATE ||
            event.published
        ) {
            nextEvent.eventId = 0; // reasons
            nextEvent.eventTypeId = CoachingEventType.COMMITMENT_UPDATE;
            nextEvent.updatedOn = new Date();

            // this is a patch
            const duration = (model.duration || 0) * 1000;
            nextEvent.sessionDate = new Date(Date.now() - duration);
            nextEvent.startTime = new Date(Date.now() - duration);
            nextEvent.duration = model.duration || 0;
        }
    }
    if (!event.published) {
        nextEvent.updatedOn = new Date();
    }
    const linkedInteractions = pluck<InteractionSummaryDto[]>(
        model.observations,
        'focusContainers[].interactions[]',
    ).map<LinkedInteractionDto>((e) => ({
        interactionId: e.interactionId,
        linkedOn: e.linkedOn || new Date(),
        diffItemId: e.diffItemId,
    }));

    const goals = (
        model.goalsCollection ? model.goalsCollection.commitmentKpis : []
    )
        .filter((e) => e.goalValue != null)
        .filter((e) => {
            /* also see GoalService.SyncStatus in the api */
            /* associateToParentFocus is only persisted for drafts */

            const next =
                (e.associateToParentFocus /* to be shared */ ||
                    e.goalId == null ||
                    e.goalId === 0) &&
                e.statusId !== GoalStatus.DELETED;
            const existing =
                e.goalId != null &&
                e.goalId !== 0 &&
                commitment.statusId !== CoachingEventStatus.DRAFT;

            return next || existing;
        })
        .map<GoalModel>((e) => {
            let createdOn = e.createdOn;
            let updatedOn = e.updatedOn;

            if (!e.isOriginalGoal) {
                createdOn = e.createdOn || new Date();
                updatedOn = e.updatedOn || (e.createdOn ? new Date() : null);
            }

            return {
                goalId: e.goalId != null ? e.goalId : 0, // reasons
                userId: performerId,
                metricId: e.metricId,
                statusId: e.statusId,
                goalValue: e.goalValue,
                estimatedCompletionDate: e.estimatedCompletionDate,
                associateToParentFocus: e.associateToParentFocus,
                shared: e.shared,
                createdOn,

                // TODO fix CommitmentKpi
                createdBy: e.createdById != null ? e.createdById : userId,
                // updatedBy: e.updatedBy != null ? e.updatedBy : (e.createdOn ? userId : null),

                updatedOn,
                isOriginalGoal: e.isOriginalGoal,
                diffItemId: e.diffItemId,
            };
        });

    let notes = model.notes.map(noteDetailFormValueReverseTransformer);
    notes = manageNotes(
        notes,
        performerId,
        false, // TODO revise. always false. needs to be explicitly set to true
        true,
    );

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const placeholderMetrics =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model && model.goalsCollection && model.goalsCollection.focusKpis
            ? model.goalsCollection.focusKpis.map((metric) => metric.metricId)
            : [];

    const nextCommitment: CommitmentPostDto = {
        commitmentId:
            commitment.commitmentId != null ? commitment.commitmentId : 0, // reasons
        performerId: commitment.performerId,
        behaviour: model.focus.behaviourId,
        subBehaviour: model.focus.subBehaviourId,
        metricId: undefined,
        queueId: undefined,
        statusId: commitment.statusId,
        events: [nextEvent],
        notes,
        linkedInteractions,
        goals,
        placeholderMetrics,
        startDate: new Date(format(model.date, 'yyyy-MM-dd')),
        endDate: new Date('2999-12-31'),
        createdBy: commitment.createdBy != null ? commitment.createdBy : userId,

        // TODO fix CommitmentDto
        // createdOn: commitment.createdOn || new Date(),
        // updatedBy: commitment.updatedBy != null ? commitment.updatedBy : (commitment.createdOn ? userId : null),
        // updatedOn: commitment.updatedOn || (commitment.createdOn ? new Date() : null),
        createdOn: new Date(),
        updatedBy: userId,
        updatedOn: new Date(),

        focusId: model.focus.focusId,
        previousDraftId: model.previousDraftId,
    };

    return nextCommitment;
}
